import { useAccessibility } from '../../context/AccessibilityContext';
import { playSound } from '../../utils/sound';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { GiftIcon } from '@heroicons/react/24/solid';
import { formatCurrency } from '../../utils/formatting';

export default function HighlightCard({ highlight }) {
  const { addToCart } = useCart();
  const { soundEnabled } = useAccessibility();

  // Check if it's an organization campaign - convert ID to string first
  const isOrganization = highlight.id && String(highlight.id).startsWith('org-');
  
  // Determine display name based on campaign type
  const displayName = isOrganization 
  ? highlight.organization_name || `${highlight.first_name} ${highlight.last_name}`.trim()
  : `${highlight.first_name || ''} ${highlight.last_name || ''}`.trim();
  
  const campaignTitle = highlight.product_name || "Untitled Campaign";
  
  // Create different descriptions based on campaign type
 const campaignDescription = isOrganization 
  ? `Support ${displayName}'s campaign for ${highlight.quantity} ${campaignTitle}${highlight.quantity > 1 ? 's' : ''}.`
  : `Help ${displayName || 'a recipient'} get a ${campaignTitle}.`;
  
  const productPrice = highlight.product_price || 0;
  const imageUrl = highlight.product_image_url || `https://via.placeholder.com/400x300.png?text=${campaignTitle.replace(/ /g, '+')}`;

  const handleGiftNow = (e) => {
    e.stopPropagation();
    playSound('add-to-cart.mp3', soundEnabled);
    
    if (isOrganization) {
      // For organization campaigns, show a message instead of adding to cart
      alert('Organization campaigns work differently. Please visit the campaign page for more information.');
      return;
    }
    
    addToCart(highlight);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden flex flex-col transform hover:-translate-y-2 transition-transform duration-300">
      
      {/* Organization badge */}
      {isOrganization && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full z-10">
          Organization
        </div>
      )}
      
      {/* Image Section */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 p-4 relative"> 
        <img 
          src={imageUrl} 
          alt={campaignTitle} 
          className="w-full h-48 object-contain" 
        />
      </div>

      {/* Content Section */}
      <div className="p-6 flex flex-col flex-grow text-left">
        <p className="text-sm font-semibold text-orange-500 dark:text-orange-400 mb-1">
          {isOrganization ? 'Organization' : 'Recipient'}
        </p>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {campaignTitle}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 flex-grow text-sm min-h-[40px]">
          {`${campaignDescription.substring(0, 90)}...`}
        </p>

        {/* Progress bar for organization campaigns */}
        {isOrganization && highlight.units_fulfilled > 0 && (
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Progress</span>
              <span>{highlight.units_fulfilled || 0} / {highlight.quantity}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ 
                  width: `${((highlight.units_fulfilled || 0) / highlight.quantity) * 100}%` 
                }}
              ></div>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isOrganization ? 'Price per unit' : 'Price'}
            </p>
            <p className="text-xl font-bold text-orange-500 dark:text-orange-400">
              {formatCurrency(productPrice)}
            </p>
            
            {/* Total amount for organization campaigns */}
            {isOrganization && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Total: {formatCurrency(highlight.total_price)}
              </p>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={handleGiftNow}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-3 rounded-lg transition-colors flex items-center justify-center text-sm"
            >
              <GiftIcon className="w-4 h-4 mr-2"/>
              {isOrganization ? 'Donate' : 'Gift Now'}
            </button>
            <Link
              to={`/campaigns/${highlight.id}`}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 font-semibold py-2 px-3 rounded-lg transition-colors text-center text-sm"
            >
              View
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}